import React, { useEffect } from "react";
import {
  Alert,
  AlertIcon,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  HStack,
  FormErrorMessage,
  Spinner,
  Text,
  Box,
  SimpleGrid,
  useColorModeValue,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { Role, User } from "../../types";
import { roles } from "../../services/api";
import { UserFormData } from "./types";
import { ACCESS_GRANT_DEFINITIONS } from "../../constants/accessGrants";
import { IconToggleCard } from "./IconToggleCard";
import {
  getUserIdentifier,
  getUserIdentifierLabel,
} from "../../utils/userIdentity";

interface UserFormProps {
  initialData?: Partial<User>;
  onSubmit: (data: UserFormData) => void;
  isLoading?: boolean;
  onCancel: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const initialRoleValue =
    typeof initialData?.role === "string"
      ? initialData.role
      : initialData?.role?._id || "";
  const initialAccessGrants = initialData?.accessGrants || [];
  const isEditMode = !!initialData?._id;
  const roleCardBorderColor = useColorModeValue("gray.200", "gray.700");
  const roleCardBg = useColorModeValue("white", "gray.800");
  const roleCardSelectedBg = useColorModeValue("blue.50", "blue.900");
  const roleCardSelectedBorder = useColorModeValue("blue.400", "blue.300");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<UserFormData>({
    defaultValues: {
      nationalId: initialData?.nationalId || "",
      fullName: initialData?.fullName || "",
      mobile: initialData?.mobile || "",
      role: initialRoleValue,
      accessGrants: initialAccessGrants,
    },
  });

  const { data: rolesResponse, isLoading: isLoadingRoles } = useQuery({
    queryKey: ["roles", "all-for-users"],
    queryFn: () => roles.getAll(1, 100) as Promise<any>,
  });

  const roleItems: Role[] = rolesResponse?.roles || [];
  const selectedRole = watch("role");
  const selectedAccessGrants = watch("accessGrants") || [];

  useEffect(() => {
    register("accessGrants");
  }, [register]);

  const toggleAccessGrant = (grantKey: string) => {
    const nextAccessGrants = selectedAccessGrants.includes(grantKey)
      ? selectedAccessGrants.filter((item) => item !== grantKey)
      : [...selectedAccessGrants, grantKey];

    setValue("accessGrants", nextAccessGrants, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4}>
        {isEditMode ? (
          <>
            <VStack
              w="100%"
              align="stretch"
              spacing={1}
              borderWidth="1px"
              borderRadius="xl"
              borderColor={roleCardBorderColor}
              p={4}
            >
              <Text fontWeight="semibold">{initialData?.fullName || "—"}</Text>
              <Text fontSize="sm" color="gray.500">
                {getUserIdentifierLabel(initialData)}:{" "}
                {getUserIdentifier(initialData) || "—"}
              </Text>
            </VStack>

            <FormControl isInvalid={!!errors.mobile}>
              <FormLabel>شماره تلفن</FormLabel>
              <Input
                {...register("mobile", {
                  required: "شماره تلفن الزامی است",
                })}
              />
              <FormErrorMessage>
                {errors.mobile && errors.mobile.message}
              </FormErrorMessage>
            </FormControl>
          </>
        ) : (
          <>
            <FormControl isInvalid={!!errors.nationalId}>
              <FormLabel>کد ملی</FormLabel>
              <Input
                {...register("nationalId", {
                  required: "کد ملی الزامی است",
                })}
                isDisabled={!!initialData?._id}
              />
              <FormErrorMessage>
                {errors.nationalId && errors.nationalId.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.fullName}>
              <FormLabel>نام و نام خانوادگی</FormLabel>
              <Input
                {...register("fullName", {
                  required: "نام و نام خانوادگی الزامی است",
                })}
              />
              <FormErrorMessage>
                {errors.fullName && errors.fullName.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.mobile}>
              <FormLabel>موبایل</FormLabel>
              <Input
                {...register("mobile", {
                  required: "موبایل الزامی است",
                })}
              />
              <FormErrorMessage>
                {errors.mobile && errors.mobile.message}
              </FormErrorMessage>
            </FormControl>
          </>
        )}

        <FormControl isInvalid={!!errors.role}>
          <FormLabel>نقش</FormLabel>
          <input
            type="hidden"
            {...register("role", {
              required: "نقش الزامی است",
            })}
          />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            {roleItems.map((role) => {
              const isSelected = selectedRole === role._id;

              return (
                <Box
                  key={role._id}
                  borderWidth="1px"
                  borderRadius="xl"
                  borderColor={
                    isSelected ? roleCardSelectedBorder : roleCardBorderColor
                  }
                  bg={isSelected ? roleCardSelectedBg : roleCardBg}
                  px={4}
                  py={3}
                  cursor={isLoadingRoles ? "not-allowed" : "pointer"}
                  opacity={isLoadingRoles ? 0.6 : 1}
                  onClick={() => {
                    if (!isLoadingRoles) {
                      setValue("role", role._id, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }
                  }}
                >
                  <Text fontWeight="semibold">{role.label}</Text>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    {role.value}
                  </Text>
                </Box>
              );
            })}
          </SimpleGrid>
          <FormErrorMessage>
            {errors.role && errors.role.message}
          </FormErrorMessage>
        </FormControl>

        {isLoadingRoles ? (
          <HStack width="100%" color="gray.500" justify="flex-start">
            <Spinner size="sm" />
            <Text fontSize="sm">در حال دریافت نقش‌ها</Text>
          </HStack>
        ) : null}

        <FormControl>
          <FormLabel>دسترسی‌های ویژه</FormLabel>
          <Alert status="info" borderRadius="xl" mb={3}>
            <AlertIcon />
            این دسترسی‌ها فقط محدودیت فیلتر مالکیت داده را برمی‌دارند و کاربر
            را سوپر ادمین نمی‌کنند.
          </Alert>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            {ACCESS_GRANT_DEFINITIONS.map((grant) => (
              <IconToggleCard
                key={grant.key}
                icon={grant.icon}
                isChecked={selectedAccessGrants.includes(grant.key)}
                label={grant.label}
                name={grant.key}
                onChange={() => toggleAccessGrant(grant.key)}
              />
            ))}
          </SimpleGrid>
          <Box width="100%" mt={3}>
            {ACCESS_GRANT_DEFINITIONS.filter((grant) =>
              selectedAccessGrants.includes(grant.key),
            ).map((grant) => (
              <Text key={grant.key} fontSize="sm" color="gray.500" mt={1}>
                {grant.description}
              </Text>
            ))}
          </Box>
        </FormControl>

        <HStack spacing={4} width="100%" justify="flex-end">
          <Button onClick={onCancel}>انصراف</Button>
          <Button type="submit" colorScheme="blue">
            {isEditMode ? "ذخیره تغییرات" : "ایجاد"}
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};
